import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Loader2, Disc3, Wind, Activity } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Reveal, Eyebrow } from "./Reveal";
import { API } from "../constants/brand";

const SERVICES = [
  {
    id: "radwechsel",
    icon: Disc3,
    title: "Radwechsel & Wuchten",
    price: "ab 39 €",
    desc: "Sommer- und Winterwechsel inklusive präzisem Auswuchten — für ruhiges Fahrverhalten und weniger Reifenverschleiß. Gib einfach deine Reifengröße an.",
    image:
      "https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6MTJ8MHwxfHNlYXJjaHwxfHxhdXRvJTIwcmVwYWlyJTIwd29ya3Nob3AlMjB3aGVlbCUyMHNlcnZpY2UlMjBtZWNoYW5pYyUyMGNhciUyMGVuZ2luZXxlbnwwfHx8fDE3OTE0OTQ0Mzh8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "motorreinigung",
    icon: Wind,
    title: "Wasserstoff-Motorreinigung",
    price: "ab 129 €",
    desc: "Carbon Cleaning mit HHO-Gas: löst schonend Ablagerungen an Kolben, Ventilen, AGR-Ventil und Turbolader — ganz ohne Demontage. Ruhigerer Motorlauf, besseres Ansprechverhalten, weniger Verbrauch und Emissionen. Ideal vor der Abgasuntersuchung (AU).",
    image:
      "https://images.unsplash.com/photo-1676018366904-c083ed678e60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6MTJ8MHwxfHNlYXJjaHw0fHxhdXRvJTIwcmVwYWlyJTIwd29ya3Nob3AlMjB3aGVlbCUyMHNlcnZpY2UlMjBtZWNoYW5pYyUyMGNhciUyMGVuZ2luZXxlbnwwfHx8fDE3OTE0OTQ0Mzh8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "fahrwerk",
    icon: Activity,
    title: "Fahrwerk-Straßensimulator",
    price: "ab 80 €",
    desc: "Die ultimative Diagnose für Fahrkomfort und Sicherheit: der Simulator bildet reale Straßenbedingungen, Schlaglöcher und Kurvenbelastungen direkt im Stand ab — Störgeräusche an Stoßdämpfern, Lenkung und Lagern werden präzise lokalisiert, ohne Rätselraten. Endpreis je nach Fahrzeug.",
    image:
      "https://images.unsplash.com/photo-1643701079732-3b1c7a797e3d?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
];

const TIME_SLOTS = ["08:00", "09:30", "11:00", "13:00", "14:30", "16:00", "17:30"];

const TIRE_SIZES = ["195/65 R15", "205/55 R16", "215/60 R16", "225/45 R17", "225/40 R18", "Andere / vor Ort klären"];

export const Services = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [service, setService] = useState(SERVICES[0].id);
  const [tireSize, setTireSize] = useState(TIRE_SIZES[1]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState(TIME_SLOTS[0]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("konto_user") || "null");
      if (u) {
        setName((v) => v || u.name || "");
        setEmail((v) => v || u.email || "");
        setPhone((v) => v || u.phone || "");
      }
    } catch {
      /* ignore */
    }
  }, []);
  const [sending, setSending] = useState(false);

  const openModal = (id) => {
    setService(id);
    setModalOpen(true);
  };

  const submit = async () => {
    if (!name.trim() || !phone.trim() || !date) {
      toast.error("Bitte Name, Telefonnummer und Datum angeben.");
      return;
    }
    setSending(true);
    try {
      const svc = SERVICES.find((s) => s.id === service);
      await axios.post(`${API}/appointments`, {
        service: svc.title,
        date,
        time,
        name,
        phone,
        email: email.trim() || null,
        tire_size: service === "radwechsel" ? tireSize : null,
      });
      toast.success(
        `Termin angefragt: ${svc.title} am ${date} um ${time} Uhr.${email.trim() ? " Bestätigung per E-Mail unterwegs." : " Wir bestätigen telefonisch."}`
      );
      setModalOpen(false);
    } catch {
      toast.error("Termin konnte nicht gespeichert werden.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="service" className="relative py-24 lg:py-32 noise-overlay">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal>
          <Eyebrow>Service-Werkstatt</Eyebrow>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-50 max-w-2xl">
            Deine Werkstatt für <span className="text-gradient-blue">Räder, Motor & Fahrwerk</span>
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl text-base">
            Radwechsel mit Auswuchten, schonende Wasserstoff-Motorreinigung (Carbon Cleaning) und Fahrwerk-Diagnose auf dem Straßensimulator — buche deinen Termin direkt online.
          </p>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.12}>
              <article className="group rounded-3xl overflow-hidden bg-[#121826] border border-white/8 hover:border-cyan-400/30 transition-colors duration-500 h-full flex flex-col">
                <div className="relative overflow-hidden aspect-[16/9]">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-108"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121826] to-transparent" />
                  <div className="absolute top-4 right-4 rounded-full bg-[#0B0F17]/80 backdrop-blur px-3 py-1 font-mono-tech text-xs font-bold text-cyan-300 border border-cyan-400/25">
                    {s.price}
                  </div>
                </div>
                <div className="p-6 flex flex-col gap-3 flex-1">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1E6FD9]/20 to-[#29B6F6]/10 border border-cyan-400/25 flex items-center justify-center">
                    <s.icon size={20} className="text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-50">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed flex-1">{s.desc}</p>
                  <button
                    data-testid={`service-book-appointment-button-${s.id}`}
                    onClick={() => openModal(s.id)}
                    className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-400/40 bg-cyan-400/10 py-3 text-sm font-bold text-cyan-300 hover:bg-cyan-400/20 transition-colors duration-300"
                  >
                    <CalendarCheck size={16} />
                    Termin buchen
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#0d1320] border border-white/10 text-slate-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Termin buchen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono-tech block mb-2">Service</label>
              <div className="grid grid-cols-1 gap-2">
                {SERVICES.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`appointment-service-${s.id}`}
                    onClick={() => setService(s.id)}
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm transition-colors duration-300 ${
                      service === s.id
                        ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200"
                        : "border-white/10 text-slate-400 hover:border-white/25"
                    }`}
                  >
                    <span className="font-semibold">{s.title}</span>
                    <span className="font-mono-tech text-xs text-cyan-300">{s.price}</span>
                  </button>
                ))}
              </div>
            </div>

            {service === "radwechsel" && (
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono-tech block mb-2">Reifengröße</label>
                <div className="flex flex-wrap gap-1.5" data-testid="tire-size-picker">
                  {TIRE_SIZES.map((s) => (
                    <button
                      key={s}
                      data-testid={`tire-size-${s.replace(/[^a-zA-Z0-9]/g, "-")}`}
                      onClick={() => setTireSize(s)}
                      className={`rounded-lg border px-3 py-2 font-mono-tech text-xs transition-colors ${
                        tireSize === s
                          ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300"
                          : "border-white/10 text-slate-400 hover:border-white/25"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono-tech block mb-2">Datum</label>
                <input
                  data-testid="appointment-date-picker"
                  type="date"
                  value={date}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-cyan-400/50 [color-scheme:dark]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono-tech block mb-2">Uhrzeit</label>
                <div className="flex flex-wrap gap-1.5">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      data-testid={`appointment-time-${t.replace(":", "")}`}
                      onClick={() => setTime(t)}
                      className={`rounded-lg border px-2.5 py-1.5 font-mono-tech text-xs transition-colors ${
                        time === t
                          ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300"
                          : "border-white/10 text-slate-400 hover:border-white/25"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <input
                data-testid="appointment-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dein Name"
                className="rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
              />
              <input
                data-testid="appointment-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Telefonnummer"
                className="rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
              />
              <input
                data-testid="appointment-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="E-Mail (optional, für Bestätigung)"
                className="col-span-2 rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
              />
            </div>

            <button
              data-testid="appointment-submit-button"
              onClick={submit}
              disabled={sending}
              className="w-full rounded-xl bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] py-3.5 text-sm font-bold text-white flex items-center justify-center gap-2 transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />}
              Termin anfragen
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
